"use client";

import { motion } from "motion/react";
import { useTranslations } from "next-intl";
import { Icon } from "@iconify/react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

// Ce que l'on construit avec React. Couleurs écrites en toutes lettres pour
// que Tailwind les génère bien (pas de classes dynamiques tronquées).
const DOMAINS = [
    {
        icon: "users-group-rounded-bold-duotone",
        titleKey: "React_everywhere_social_title",
        descKey: "React_everywhere_social_desc",
        bg: "bg-sky-500/15",
        ring: "ring-sky-400/30",
        text: "text-sky-300",
    },
    {
        icon: "chart-2-bold-duotone",
        titleKey: "React_everywhere_dashboard_title",
        descKey: "React_everywhere_dashboard_desc",
        bg: "bg-emerald-500/15",
        ring: "ring-emerald-400/30",
        text: "text-emerald-300",
    },
    {
        icon: "smartphone-bold-duotone",
        titleKey: "React_everywhere_mobile_title",
        descKey: "React_everywhere_mobile_desc",
        bg: "bg-violet-500/15",
        ring: "ring-violet-400/30",
        text: "text-violet-300",
    },
    {
        icon: "cart-large-2-bold-duotone",
        titleKey: "React_everywhere_ecommerce_title",
        descKey: "React_everywhere_ecommerce_desc",
        bg: "bg-amber-500/15",
        ring: "ring-amber-400/30",
        text: "text-amber-300",
    },
    {
        icon: "magic-stick-3-bold",
        titleKey: "React_everywhere_animated_title",
        descKey: "React_everywhere_animated_desc",
        bg: "bg-rose-500/15",
        ring: "ring-rose-400/30",
        text: "text-rose-300",
    },
    {
        icon: "clipboard-list-bold-duotone",
        titleKey: "React_everywhere_forms_title",
        descKey: "React_everywhere_forms_desc",
        bg: "bg-cyan-500/15",
        ring: "ring-cyan-400/30",
        text: "text-cyan-300",
    },
    {
        icon: "widget-5-bold-duotone",
        titleKey: "React_everywhere_saas_title",
        descKey: "React_everywhere_saas_desc",
        bg: "bg-indigo-500/15",
        ring: "ring-indigo-400/30",
        text: "text-indigo-300",
    },
    {
        icon: "palette-round-bold-duotone",
        titleKey: "React_everywhere_design_title",
        descKey: "React_everywhere_design_desc",
        bg: "bg-orange-500/15",
        ring: "ring-orange-400/30",
        text: "text-orange-300",
    },
];

// Apps mondialement connues construites avec React (vrais logos via Iconify).
const COMPANIES = [
    { name: "Facebook", logo: "logos:facebook" },
    { name: "Instagram", logo: "logos:instagram-icon" },
    { name: "Netflix", logo: "logos:netflix-icon" },
    { name: "TikTok", logo: "logos:tiktok-icon" },
    { name: "Airbnb", logo: "logos:airbnb-icon" },
    { name: "Discord", logo: "logos:discord-icon" },
    { name: "WhatsApp", logo: "logos:whatsapp-icon" },
    { name: "Spotify", logo: "logos:spotify-icon" },
];

const gridContainer = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.08, delayChildren: 0.12 } },
};

const cardItem = {
    hidden: { opacity: 0, y: 16, scale: 0.96 },
    visible: {
        opacity: 1,
        y: 0,
        scale: 1,
        transition: { duration: 0.45, ease: "easeOut" },
    },
};

export default function ReactEverywhere() {
    const t = useTranslations();

    return (
        <section id="react-everywhere" className="pt-16">
            <h2
                className="text-2xl dark:text-white font-bold font-sourceSans"
                style={{ marginBottom: "0.5em" }}
            >
                {t("React_everywhere_title")}
            </h2>
            <div className="w-20 h-1 bg-primary rounded mt-3 mb-5"></div>

            <p className="text-black dark:text-lightBlue max-w-[700px] leading-7 font-geistSans mb-0">
                {t("React_everywhere_intro")}
            </p>

            <div className="mt-10 relative rounded-3xl overflow-hidden bg-gradient-to-br from-bgDark-700 via-bgDark-800 to-bgDark-900 ring-1 ring-white/10 p-8 sm:p-12">
                {/* Glows */}
                <div className="absolute -top-24 -right-20 w-[460px] h-[460px] bg-sky-500/20 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute -bottom-24 -left-20 w-[380px] h-[380px] bg-cyan-500/15 rounded-full blur-3xl pointer-events-none" />

                <div className="relative">
                    {/* Eyebrow */}
                    <div className="inline-flex items-center gap-2 rounded-full bg-sky-500/15 ring-1 ring-sky-400/30 px-3 py-1 mb-4">
                        <SolarIcon
                            name="global-bold-duotone"
                            className="w-3.5 h-3.5 text-sky-300"
                        />
                        <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-sky-300">
                            {t("React_everywhere_eyebrow")}
                        </span>
                    </div>

                    {/* Headline */}
                    <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white leading-[1.12] tracking-tight mb-8 max-w-[640px]">
                        {t.rich("React_everywhere_headline", {
                            highlight: (chunks) => (
                                <span className="bg-gradient-to-r from-sky-300 to-cyan-300 bg-clip-text text-transparent">
                                    {chunks}
                                </span>
                            ),
                        })}
                    </h3>

                    {/* Grille des domaines */}
                    <motion.div
                        variants={gridContainer}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, margin: "-80px" }}
                        className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-5"
                    >
                        {DOMAINS.map((domain) => (
                            <motion.div
                                key={domain.titleKey}
                                variants={cardItem}
                                className="rounded-2xl bg-white/[0.04] ring-1 ring-white/10 p-5 hover:bg-white/[0.07] transition-colors duration-200"
                            >
                                <div
                                    className={`w-11 h-11 rounded-xl ${domain.bg} ring-1 ${domain.ring} flex items-center justify-center mb-4`}
                                >
                                    <SolarIcon
                                        name={domain.icon}
                                        className={`w-5 h-5 ${domain.text}`}
                                    />
                                </div>
                                <h4 className="text-base font-semibold text-white mb-1 leading-tight">
                                    {t(domain.titleKey)}
                                </h4>
                                <p className="text-sm text-gray-300/80 leading-relaxed mb-0">
                                    {t(domain.descKey)}
                                </p>
                            </motion.div>
                        ))}
                    </motion.div>

                    {/* Preuve sociale : les apps construites avec React */}
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-60px" }}
                        transition={{ duration: 0.5, ease: "easeOut", delay: 0.1 }}
                        className="mt-8 rounded-2xl bg-white/[0.04] ring-1 ring-white/10 px-5 py-5"
                    >
                        <div className="flex items-start gap-3">
                            <SolarIcon
                                name="verified-check-bold"
                                className="w-5 h-5 text-sky-300 shrink-0 mt-0.5"
                            />
                            <p className="text-sm text-gray-300/90 leading-relaxed mb-0">
                                {t("React_everywhere_social_proof")}
                            </p>
                        </div>
                        <div className="mt-4 flex flex-wrap items-center gap-2.5">
                            {COMPANIES.map((company) => (
                                <span
                                    key={company.name}
                                    title={company.name}
                                    className="inline-flex items-center justify-center h-9 w-12 rounded-md bg-white ring-1 ring-black/5"
                                >
                                    <Icon
                                        icon={company.logo}
                                        className="w-[18px] h-[18px]"
                                        aria-label={company.name}
                                    />
                                </span>
                            ))}
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
